import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FileText, Link2, User, Tag, Layout, Database, Code, Globe } from 'lucide-react';
import { MarkdownContent } from '@/components/ui/markdown-content';

interface DetailsItemProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  className?: string;
}

export const DetailsItem = ({ icon, label, children, className }: DetailsItemProps) => {
  return (
    <div className={cn("flex items-start", className)}>
      <div className="mr-3 mt-0.5 text-gray-400">{icon}</div>
      <div className="flex-1">
        <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
        <div>{children}</div>
      </div>
    </div>
  );
};

interface DetailsSectionProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const DetailsSection = ({ title, children, className }: DetailsSectionProps) => {
  return (
    <div className={cn("mb-6", className)}>
      {title && <h3 className="text-sm font-medium text-gray-700 mb-3">{title}</h3>}
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
};

interface DetailsBadgeListProps {
  items?: string[];
  icon: React.ReactNode;
  label: string;
}

export const DetailsBadgeList = ({ items, icon, label }: DetailsBadgeListProps) => {
  if (!items || items.length === 0) return null;
  
  return (
    <DetailsItem icon={icon} label={label}>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {items.map((item, index) => (
          <Badge key={index} variant="outline" className="px-2 py-0.5 text-xs">
            {item}
          </Badge>
        ))}
      </div>
    </DetailsItem>
  );
};

interface DetailsLinkProps {
  href?: string;
  label: string;
}

export const DetailsLink = ({ href, label }: DetailsLinkProps) => {
  if (!href) return null;
  
  return (
    <DetailsItem icon={<Link2 size={16} />} label={label}>
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline text-sm break-all"
      >
        {href}
      </a>
    </DetailsItem>
  );
};

interface DetailsTextProps {
  text?: string;
  label: string;
  icon: React.ReactNode;
  className?: string;
}

export const DetailsText = ({ text, label, icon, className }: DetailsTextProps) => {
  if (!text) return null;
  
  return (
    <DetailsItem icon={icon} label={label} className={className}>
      <p className="text-sm whitespace-pre-wrap">{text}</p>
    </DetailsItem>
  );
};

interface DetailsNotesProps {
  notes?: string;
}

export const DetailsNotes = ({ notes }: DetailsNotesProps) => {
  if (!notes) return null;
  
  return (
    <div className="mt-4">
      <MarkdownContent content={notes} />
    </div>
  );
};

export const DetailsDivider = () => {
  return <div className="border-t border-gray-100 my-4" />;
};

// Export common icons for convenience
export const DetailsIcons = {
  FileText,
  Link2,
  User,
  Tag,
  Layout,
  Database,
  Code,
  Globe
};
